import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BookOpenText,
  CheckCircle2,
  ChevronRight,
  CircleSlash,
  Clipboard,
  KeyRound,
  ListFilter,
  LogOut,
  RefreshCw,
  ShieldCheck,
  SlidersHorizontal,
  Swords
} from 'lucide-react';
import { clearToken, fetchAdminConfig, fetchAudit, fetchAudits, fetchModels, getToken, login, setToken } from './api';
import type { AdminConfig, AuditRecord, AuditorRun, Candidate, Diagnostics, ListResponse, ModelInfo, Winner } from './types';

const statusLabels: Record<string, string> = {
  pending: 'В очереди',
  completed: 'Завершено',
  partially_completed: 'Частично завершено',
  failed: 'Ошибка'
};

const statusHints: Record<string, string> = {
  pending: 'Аудит создан, но проверка еще не завершилась.',
  completed: 'Все выбранные аудиторы успешно вернули результат.',
  partially_completed: 'Часть аудиторов завершилась успешно, часть вернула ошибку.',
  failed: 'Ни один аудитор не дал пригодный результат.'
};

const winnerLabels: Record<string, string> = {
  baseline: 'Базовый ответ',
  optimized: 'Оптимизированный ответ',
  tie: 'Ничья'
};

const winnerHints: Record<string, string> = {
  baseline: 'Лучше прошел сырой базовый ответ, построенный на полном контексте.',
  optimized: 'Лучше прошел оптимизированный ответ, построенный на сжатом контексте.',
  tie: 'Аудитор не увидел значимой разницы между ответами.'
};

const contextLabels: Record<string, string> = {
  full_context: 'полный контекст',
  reduced_context: 'сжатый контекст',
  unknown: 'контекст не указан'
};

const consensusLabels: Record<string, string> = {
  high: 'Высокое',
  medium: 'Среднее',
  low: 'Низкое'
};

export default function App() {
  const [token, setAuthToken] = useState(getToken());

  const handleLogout = useCallback(() => {
    clearToken();
    setAuthToken('');
  }, []);

  return (
    <Routes>
      <Route path="/docs" element={<PublicDocs />} />
      <Route path="/login" element={<Login onLogin={setAuthToken} />} />
      <Route
        path="/*"
        element={token ? <Shell onLogout={handleLogout} /> : <Navigate to="/login" replace />}
      />
    </Routes>
  );
}

function Login({ onLogin }: { onLogin: (token: string) => void }) {
  const [email, setEmail] = useState('admin@audit.rout.my');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const token = await login(email, password);
      setToken(token);
      onLogin(token);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось войти в панель');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-screen bg-paper text-ink">
      <div className="grid min-h-screen lg:grid-cols-[1fr_440px]">
        <section className="hidden border-r border-line bg-ink p-12 text-paper lg:flex lg:flex-col lg:justify-between">
          <div className="flex items-center gap-3 text-sm uppercase text-paper/70">
            <ShieldCheck className="h-5 w-5" />
            Сервис аудита ИИ
          </div>
          <div className="max-w-2xl">
            <div className="mb-8 h-px w-32 bg-rust" />
            <h1 className="max-w-xl text-5xl font-semibold leading-tight">
              Контроль качества ответов и контекста
            </h1>
          </div>
          <div className="text-sm text-paper/70">Рабочая панель администрирования</div>
        </section>
        <section className="flex items-center justify-center p-6">
          <form onSubmit={submit} className="w-full max-w-sm border border-line bg-white p-6 shadow-sm">
            <div className="mb-6 flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center bg-ink text-paper">
                <KeyRound className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Вход в панель</h2>
                <p className="text-sm text-ink/60">Сервис аудита ИИ</p>
              </div>
            </div>
            <label className="mb-4 block">
              <Tooltip text="Почта администратора, для которой сервер выпускает сессионный токен.">
                <span className="mb-1 block text-sm font-medium">Почта администратора</span>
              </Tooltip>
              <input
                className="input"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                type="email"
                autoComplete="email"
                placeholder="admin@audit.rout.my"
              />
            </label>
            <label className="mb-5 block">
              <Tooltip text="Пароль администратора хранится на сервере и нужен только для входа в панель.">
                <span className="mb-1 block text-sm font-medium">Пароль</span>
              </Tooltip>
              <input
                className="input"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                type="password"
                autoComplete="current-password"
                placeholder="Введите пароль"
              />
            </label>
            {error && <div className="mb-4 border border-rust/40 bg-rust/10 p-3 text-sm text-rust">{error}</div>}
            <Tooltip text="Проверяет учетные данные и открывает защищенную рабочую область.">
              <button className="button-primary w-full" disabled={loading} aria-label="Войти в панель">
                {loading ? 'Входим...' : 'Войти'}
              </button>
            </Tooltip>
            <Tooltip text="Открыть публичную инструкцию по API, токенам, моделям и статусам аудита.">
              <Link to="/docs" className="mt-3 inline-flex w-full items-center justify-center gap-2 border border-line px-4 py-2 text-sm font-medium text-ink transition hover:border-ink">
                <BookOpenText className="h-4 w-4" />
                Документация
              </Link>
            </Tooltip>
          </form>
        </section>
      </div>
    </main>
  );
}

function Shell({ onLogout }: { onLogout: () => void }) {
  return (
    <main className="min-h-screen bg-paper text-ink">
      <header className="sticky top-0 z-20 border-b border-line bg-paper/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <Tooltip text="Вернуться к журналу всех аудитов.">
            <Link to="/" className="flex min-w-0 items-center gap-3">
              <div className="grid h-9 w-9 shrink-0 place-items-center bg-ink text-paper">
                <Swords className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="soft-wrap text-base font-semibold">Сервис аудита ИИ</div>
                <div className="soft-wrap text-xs uppercase text-ink/50">Рабочая область аудита</div>
              </div>
            </Link>
          </Tooltip>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
            <Tooltip text="Публичная инструкция по работе с платформой и API.">
              <Link className="button-ghost" to="/docs" aria-label="Открыть документацию">
                <BookOpenText className="h-4 w-4" />
                Документация
              </Link>
            </Tooltip>
            <Tooltip text="Открыть интеграции, Bearer-токен и список доступных моделей rout.my.">
              <Link className="button-ghost" to="/settings" aria-label="Настройки интеграции">
                <SlidersHorizontal className="h-4 w-4" />
                Настройки
              </Link>
            </Tooltip>
            <Tooltip text="Удалить сессионный токен из браузера и вернуться на экран входа.">
              <button className="button-ghost" onClick={onLogout} aria-label="Выйти из панели">
                <LogOut className="h-4 w-4" />
                Выйти
              </button>
            </Tooltip>
          </div>
        </div>
      </header>
      <Routes>
        <Route path="/" element={<AuditHistory />} />
        <Route path="/audits/:id" element={<AuditDetail />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Routes>
    </main>
  );
}

function PublicDocs() {
  return (
    <main className="min-h-screen bg-paper text-ink">
      <header className="border-b border-line bg-paper/95">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <Link to="/docs" className="flex min-w-0 items-center gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center bg-ink text-paper">
              <BookOpenText className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="soft-wrap text-base font-semibold">Публичная документация</div>
              <div className="soft-wrap text-xs uppercase text-ink/50">Сервис аудита ИИ</div>
            </div>
          </Link>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
            <Tooltip text="Перейти на экран входа администратора.">
              <Link className="button-ghost" to="/login">
                <KeyRound className="h-4 w-4" />
                Войти
              </Link>
            </Tooltip>
          </div>
        </div>
      </header>

      <section className="mx-auto grid max-w-7xl gap-8 px-5 py-8 lg:grid-cols-[0.72fr_0.28fr]">
        <div className="space-y-6">
          <section className="border border-line bg-white p-5">
            <div className="mb-3 inline-flex max-w-full items-center gap-2 bg-ink px-3 py-1 text-xs font-semibold uppercase text-paper">
              MVP API
            </div>
            <h1 className="max-w-3xl text-3xl font-semibold leading-tight md:text-4xl">
              Документация по работе с платформой аудита
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-ink/70">
              Эта страница публичная и не требует входа. Она объясняет, как отправлять аудит,
              где брать токен, как выбирать модели rout.my и как читать итоговые статусы.
              Секреты и реальные ключи здесь не публикуются.
            </p>
          </section>

          <DocsSection title="1. Авторизация API" tooltip="API закрыт Bearer-токеном. Сам токен виден только авторизованному администратору в настройках.">
            <p>
              Все рабочие API-запросы выполняются с заголовком <Code>Authorization</Code>.
              Значение токена берется в админке: <Code>Настройки / Токен API</Code>.
            </p>
            <CodeBlock>{`Authorization: Bearer <BEARER_TOKEN>`}</CodeBlock>
          </DocsSection>

          <DocsSection title="2. Как отправить аудит" tooltip="Минимальный сценарий: передать запрос, полный контекст, сжатый контекст и два ответа-кандидата.">
            <p>
              Основной endpoint создает аудит и сразу возвращает результат в режиме <Code>sync</Code>.
              Сервис сравнивает два смысловых кандидата: <Code>baseline</Code> для сырого ответа на полном контексте
              и <Code>optimized</Code> для ответа после сжатого контекста. Порядок массива не важен, если переданы
              <Code>id</Code> и <Code>context_used</Code>.
            </p>
            <CodeBlock>{`curl https://audit.rout.my/api/v1/audits \\
  -H "Authorization: Bearer <BEARER_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "mode": "sync",
    "external_id": "case-001",
    "language": "ru",
    "user_query": "Кто говорит и что обещал?",
    "full_context": "Мира-картограф говорит в таверне. Она обещала принести карту и предупредила, что северная дорога опасна.",
    "reduced_context": {
      "content": "В таверне говорит Мира. Она обещала карту."
    },
    "candidates": [
      {
        "id": "optimized",
        "label": "Оптимизированный ответ",
        "model_name": "openai/gpt-5.4-mini",
        "context_used": "reduced_context",
        "answer": "Говорит Мира-картограф. Она обещала принести карту."
      },
      {
        "id": "baseline",
        "label": "Базовый ответ",
        "model_name": "anthropic/claude-sonnet-4.6",
        "context_used": "full_context",
        "answer": "Говорит Мира-картограф. Она обещала принести карту и предупредила об опасной северной дороге."
      }
    ]
  }'`}</CodeBlock>
          </DocsSection>

          <DocsSection title="3. Асинхронный режим" tooltip="Подходит для долгих проверок или нескольких моделей-аудиторов: API сразу возвращает audit_id, а результат забирается отдельным запросом.">
            <p>
              Для долгих проверок передайте <Code>"mode": "async"</Code>. Сервис сохранит запись со статусом
              <Code>pending</Code>, вернет <Code>audit_id</Code> и продолжит проверку в фоне.
            </p>
            <CodeBlock>{`curl https://audit.rout.my/api/v1/audits \\
  -H "Authorization: Bearer <BEARER_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{"mode":"async","user_query":"...","full_context":"...","reduced_context":{"content":"..."},"candidates":[{"answer":"..."},{"answer":"..."}]}'

curl https://audit.rout.my/api/v1/audits/<AUDIT_ID> \\
  -H "Authorization: Bearer <BEARER_TOKEN>"`}</CodeBlock>
          </DocsSection>

          <DocsSection title="4. Выбор модели" tooltip="Можно явно указать одну или несколько моделей из списка rout.my. Если поле не передано, сервер использует модель по умолчанию.">
            <p>
              Список доступных моделей подтягивается сервером из rout.my. В админке он виден в разделе
              <Code>Настройки / Модели rout.my</Code>. Для запроса используйте поле <Code>auditor_models</Code>.
            </p>
            <CodeBlock>{`{
  "auditor_models": ["anthropic/claude-haiku-4.5"]
}`}</CodeBlock>
          </DocsSection>

          <DocsSection title="5. Пример запроса списка моделей" tooltip="Публичная документация показывает маршрут, но сам запрос требует Bearer-токен.">
            <p>
              Прямой список моделей закрыт авторизацией, потому что backend ходит к партнерскому API с серверным ключом.
            </p>
            <CodeBlock>{`curl https://audit.rout.my/api/v1/models \\
  -H "Authorization: Bearer <BEARER_TOKEN>"`}</CodeBlock>
          </DocsSection>

          <DocsSection title="6. Коды статусов" tooltip="Статус показывает состояние выполнения аудита, а победитель показывает более качественный ответ.">
            <div className="grid gap-3 md:grid-cols-2">
              <DocsTerm term="completed" description="Все аудиторы завершились успешно, агрегированный результат готов." />
              <DocsTerm term="partially_completed" description="Хотя бы один аудитор завершился успешно, но часть моделей вернула ошибку." />
              <DocsTerm term="failed" description="Ни один аудитор не вернул пригодный результат для агрегации." />
              <DocsTerm term="pending" description="Запись создана, проверка еще не завершена." />
            </div>
          </DocsSection>

          <DocsSection title="7. Как читать результат" tooltip="Главные поля результата нужны для сравнения качества ответов и качества сжатого контекста.">
            <div className="grid gap-3 md:grid-cols-2">
              <DocsTerm term="winner" description="Победитель: baseline означает базовый ответ на полном контексте, optimized означает ответ на сжатом контексте, tie означает ничью." />
              <DocsTerm term="baseline_score" description="Итоговая оценка базового ответа по шкале от 0 до 100." />
              <DocsTerm term="optimized_score" description="Итоговая оценка оптимизированного ответа по шкале от 0 до 100." />
              <DocsTerm term="reduced_context_score" description="Оценка сжатого контекста: насколько он сохранил критичную информацию." />
              <DocsTerm term="aggregate_result.failure_analysis" description="Пояснение, почему слабый ответ проиграл: например, сломал роль, потерял факт, задал лишний вопрос или добавил неподтвержденную деталь." />
              <DocsTerm term="aggregate_result.fix_plan" description="Примерный план исправления: короткие шаги, по которым можно переписать ответ или поправить сжатый контекст." />
              <DocsTerm term="aggregate_result.recommendations" description="Дополнительные рекомендации аудиторов, не смешанные с планом фикса." />
            </div>
          </DocsSection>

          <DocsSection title="8. Production-конфигурация" tooltip="Операторский минимум для боевого запуска: реальные секреты, партнерский ключ и модель по умолчанию.">
            <p>
              Боевой запуск должен идти с <Code>APP_ENV=production</Code>. В этом режиме backend не стартует
              с dev-токеном, коротким паролем, пустым партнерским ключом или fake-моделью по умолчанию.
            </p>
            <CodeBlock>{`APP_ENV=production
API_TOKEN=<strong-ingestion-token>
ADMIN_EMAIL=<admin-email>
ADMIN_PASSWORD=<strong-admin-password>
SESSION_SECRET=<strong-session-secret>
PARTNER_BASE_URL=https://api.rout.my
PARTNER_API_KEY=<server-side-rout-key>
DEFAULT_AUDITOR_MODELS=anthropic/claude-haiku-4.5`}</CodeBlock>
          </DocsSection>
        </div>

        <aside className="space-y-4">
          <section className="border border-line bg-white p-4">
            <h2 className="mb-3 text-sm font-semibold">Быстрые ссылки</h2>
            <div className="grid gap-2 text-sm">
              <Link className="text-signal" to="/login">Войти в админку</Link>
              <Link className="text-signal" to="/">История аудитов</Link>
              <Link className="text-signal" to="/settings">Настройки и токен</Link>
            </div>
          </section>
          <section className="border border-line bg-white p-4">
            <h2 className="mb-2 text-sm font-semibold">Базовый URL</h2>
            <div className="overflow-auto border border-line bg-paper p-3 font-mono text-xs">https://audit.rout.my/api/v1</div>
          </section>
          <section className="border border-line bg-white p-4 text-sm leading-6 text-ink/70">
            <h2 className="mb-2 text-sm font-semibold text-ink">Важно</h2>
            Bearer-токен нельзя вставлять в публичные страницы, клиентские приложения и репозитории.
            Используйте его только на backend-стороне интеграции.
          </section>
        </aside>
      </section>
    </main>
  );
}

function AuditHistory() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [status, setStatus] = useState('');
  const [winner, setWinner] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const params = useMemo(() => {
    const next = new URLSearchParams({ page: '1', limit: '50' });
    if (status) next.set('status', status);
    if (winner) next.set('winner', winner);
    if (search) next.set('search', search);
    return next;
  }, [status, winner, search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchAudits(params));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить аудиты');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="mx-auto max-w-7xl px-5 py-6">
      <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">История аудитов</h1>
          <p className="text-sm text-ink/60">{pluralAudits(data?.pagination.total ?? 0)}</p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:justify-end">
          <Tooltip text="Ищет по внешнему ID, запросу пользователя и сохраненным полям аудита.">
            <div className="relative w-full sm:w-auto">
              <ListFilter className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
              <input
                className="input h-10 w-full pl-9 sm:w-64"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Поиск по аудиту"
                aria-label="Поиск по истории аудитов"
              />
            </div>
          </Tooltip>
          <Tooltip text="Фильтрует журнал по техническому состоянию проверки.">
            <select className="select w-full sm:w-auto" value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Фильтр по статусу">
              <option value="">Все статусы</option>
              <option value="completed">Завершено</option>
              <option value="partially_completed">Частично</option>
              <option value="failed">Ошибка</option>
            </select>
          </Tooltip>
          <Tooltip text="Фильтрует журнал по ответу, который выбрал аудитор.">
            <select className="select w-full sm:w-auto" value={winner} onChange={(event) => setWinner(event.target.value)} aria-label="Фильтр по победителю">
              <option value="">Все победители</option>
              <option value="baseline">Базовый ответ</option>
              <option value="optimized">Оптимизированный ответ</option>
              <option value="tie">Ничья</option>
            </select>
          </Tooltip>
          <Tooltip text="Повторно загрузить историю с текущими фильтрами.">
            <button className="button-ghost h-10" onClick={load} aria-label="Обновить историю аудитов">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </Tooltip>
        </div>
      </div>
      {error && <Banner tone="danger" message={error} />}
      <div className="overflow-x-auto border border-line bg-white">
        <table className="w-full min-w-[980px] border-collapse text-sm">
          <thead className="bg-ink text-left text-paper">
            <tr>
              <Th tooltip="Когда аудит был создан на сервере.">Создан</Th>
              <Th tooltip="ID из внешней системы, если он был передан при создании аудита.">Внешний ID</Th>
              <Th tooltip="Итоговое состояние выполнения аудита.">Статус</Th>
              <Th tooltip="Ответ, который получил лучшую оценку аудитора.">Победитель</Th>
              <Th tooltip="Пара оценок: базовый ответ / оптимизированный ответ.">Оценки</Th>
              <Th tooltip="Оценка качества сжатого контекста.">Сжатый</Th>
              <Th tooltip="Уровень согласия аудиторов между собой.">Консенсус</Th>
              <Th tooltip="Модели rout.my или локальные аудиторы, участвовавшие в проверке.">Аудиторы</Th>
              <Th tooltip="Переход к полной карточке аудита.">Действие</Th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((audit) => (
              <tr key={audit.audit_id} className="border-t border-line hover:bg-paper/70">
                <Td>{formatDate(audit.created_at)}</Td>
                <Td>{audit.external_id || '-'}</Td>
                <Td><StatusBadge status={audit.status} /></Td>
                <Td><WinnerBadge winner={audit.winner || ''} /></Td>
                <Td>{scorePair(audit)}</Td>
                <Td>{audit.reduced_context_score ?? '-'}</Td>
                <Td>{consensusLabel(audit.consensus_level)}</Td>
                <Td>{audit.auditor_models?.join(', ') || '-'}</Td>
                <Td>
                  <Tooltip text="Открыть запрос, ответы, контексты, диагностику и сырые данные аудита.">
                    <Link className="inline-flex items-center gap-1 font-medium text-signal" to={`/audits/${audit.audit_id}`}>
                      Открыть <ChevronRight className="h-4 w-4" />
                    </Link>
                  </Tooltip>
                </Td>
              </tr>
            ))}
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-ink/55">Аудитов пока нет</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AuditDetail() {
  const { id } = useParams();
  const [audit, setAudit] = useState<AuditRecord | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    fetchAudit(id)
      .then(setAudit)
      .catch((err) => setError(err instanceof Error ? err.message : 'Не удалось загрузить аудит'));
  }, [id]);

  if (error) {
    return <section className="mx-auto max-w-7xl px-5 py-6"><Banner tone="danger" message={error} /></section>;
  }
  if (!audit) {
    return <section className="mx-auto max-w-7xl px-5 py-6 text-sm text-ink/60">Загружаем аудит...</section>;
  }
  const aggregate = audit.aggregate_result;
  const recommendations = aggregate?.recommendations ?? [];
  const fixPlan = aggregate?.fix_plan ?? [];
  const diagnostics = collectDiagnostics(audit.auditor_results ?? []);
  const resolvedWinner = (aggregate?.winner ?? audit.winner ?? '') as Winner;
  const candidates = roleCandidates(audit.candidates);

  return (
    <section className="mx-auto max-w-7xl px-5 py-6">
      <Tooltip text="Вернуться к таблице всех сохраненных аудитов.">
        <Link to="/" className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-signal">
          <ArrowLeft className="h-4 w-4" /> К истории
        </Link>
      </Tooltip>
      <div className="mb-5 grid gap-4 lg:grid-cols-[1.35fr_0.65fr]">
        <div>
          <h1 className="text-2xl font-semibold">{audit.external_id || audit.audit_id}</h1>
          <p className="mt-1 text-sm text-ink/60">{audit.user_query}</p>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <Metric label="Победитель" value={winnerLabel(resolvedWinner)} tooltip="Какой ответ признан лучшим по итоговой агрегации аудиторов." />
          <Metric label="Разница" value={aggregate?.score_delta ?? '-'} tooltip="Разница между оценками базового и оптимизированного ответа." />
          <Metric label="Базовый" value={audit.baseline_score ?? '-'} tooltip="Итоговая оценка ответа с id=baseline, обычно на полном контексте." />
          <Metric label="Оптимизированный" value={audit.optimized_score ?? '-'} tooltip="Итоговая оценка ответа с id=optimized, обычно на сжатом контексте." />
        </div>
      </div>

      {aggregate && (
        <div className="mb-5 border border-line bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <WinnerBadge winner={aggregate.winner} />
            <Tooltip text="Сводка согласия между аудиторами. Для русских аудитов сервис формирует ее на русском языке.">
              <span className="text-sm text-ink/60">{aggregate.consensus.summary}</span>
            </Tooltip>
          </div>
          <p className="text-sm leading-6 text-ink/80">{aggregate.summary}</p>
          {(aggregate.failure_analysis || fixPlan.length > 0) && (
            <div className="mt-4 grid gap-4 border-t border-line pt-4 lg:grid-cols-2">
              {aggregate.failure_analysis && (
                <div>
                  <Tooltip text="Короткое объяснение, почему проигравший или слабый ответ не прошел аудит качества.">
                    <h2 className="soft-wrap mb-2 text-sm font-semibold">Почему ответ слабый</h2>
                  </Tooltip>
                  <p className="soft-wrap text-sm leading-6 text-ink/75">{aggregate.failure_analysis}</p>
                </div>
              )}
              {fixPlan.length > 0 && (
                <div>
                  <Tooltip text="Практические шаги, по которым можно переписать ответ или поправить контекст, чтобы убрать найденные проблемы.">
                    <h2 className="soft-wrap mb-2 text-sm font-semibold">Примерный план фикса</h2>
                  </Tooltip>
                  <ol className="space-y-2 text-sm text-ink/75">
                    {fixPlan.map((item, index) => (
                      <li key={`${index}-${item}`} className="soft-wrap flex gap-2">
                        <span className="shrink-0 text-ink/45">{index + 1}.</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}
          {recommendations.length > 0 && (
            <div className="mt-4 border-t border-line pt-4">
              <Tooltip text="Дополнительные рекомендации аудиторов: что стоит изменить в ответе, сжатом контексте или правилах генерации.">
                <h2 className="soft-wrap mb-2 text-sm font-semibold">Рекомендации</h2>
              </Tooltip>
              <ul className="grid gap-2 text-sm text-ink/75 md:grid-cols-2">
                {recommendations.map((item) => (
                  <li key={item} className="soft-wrap border-l-2 border-rust/70 pl-3">{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <TextPanel title="Базовый ответ" tooltip="Кандидат с id=baseline: сырой ответ, который строится по полному контексту и служит контрольной точкой." badge={candidateBadge(candidates.baseline)} text={candidates.baseline?.answer ?? ''} />
        <TextPanel title="Оптимизированный ответ" tooltip="Кандидат с id=optimized: ответ runtime после сжатия контекста и выбранной модели маршрутизации." badge={candidateBadge(candidates.optimized)} text={candidates.optimized?.answer ?? ''} />
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <TextPanel title="Полный контекст" tooltip="Исходный контекст, который считается эталонным для проверки." text={audit.full_context} tall />
        <TextPanel title="Сжатый контекст" tooltip="Оптимизированная версия контекста, качество которой тоже оценивается." badge={`оценка ${audit.reduced_context_score ?? '-'}`} text={audit.reduced_context.content} tall />
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[0.95fr_1.05fr]">
        <section className="border border-line bg-white">
          <SectionHeader icon={<Activity className="h-4 w-4" />} title="Запуски аудиторов" tooltip="Отдельный результат каждой модели-аудитора: статус, победитель, оценки и ошибка." />
          <div className="divide-y divide-line">
            {(audit.auditor_results ?? []).map((run) => <AuditorRunRow key={run.auditor_model} run={run} />)}
          </div>
        </section>
        <section className="border border-line bg-white">
          <SectionHeader icon={<AlertTriangle className="h-4 w-4" />} title="Диагностика" tooltip="Факты, которые аудитор отметил как пропущенные, лишние или потенциально выдуманные." />
          <div className="grid gap-4 p-4 md:grid-cols-2">
            <DiagnosticList title="Потеряно в сжатом контексте" tooltip="Важные сведения из полного контекста, которых не хватает в сжатой версии." items={diagnostics.missing_from_reduced_context} />
            <DiagnosticList title="Лишнее в сжатом контексте" tooltip="Фрагменты, которые аудитор считает шумом или ненужными деталями." items={diagnostics.unnecessary_in_reduced_context} />
            <DiagnosticList title="Выдумки в базовом ответе" tooltip="Потенциальные галлюцинации или неподтвержденные факты в ответе с id=baseline." items={diagnostics.possible_hallucinations_baseline} />
            <DiagnosticList title="Выдумки в оптимизированном ответе" tooltip="Потенциальные галлюцинации или неподтвержденные факты в ответе с id=optimized." items={diagnostics.possible_hallucinations_optimized} />
          </div>
        </section>
      </div>

      <details className="mt-5 border border-line bg-white">
        <summary className="cursor-pointer px-4 py-3 text-sm font-semibold">
          <Tooltip text="Полная запись аудита как она хранится в API. Нужна для отладки интеграций.">
            <span>Исходный JSON</span>
          </Tooltip>
        </summary>
        <pre className="max-h-[520px] overflow-auto border-t border-line bg-ink p-4 text-xs text-paper">{JSON.stringify(audit, null, 2)}</pre>
      </details>
    </section>
  );
}

function SettingsPage() {
  const [config, setConfig] = useState<AdminConfig | null>(null);
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([fetchAdminConfig(), fetchModels()])
      .then(([adminConfig, providerModels]) => {
        setConfig(adminConfig);
        setModels(providerModels);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Не удалось загрузить настройки'));
  }, []);

  async function copyBearer() {
    if (!config) return;
    await navigator.clipboard.writeText(`Bearer ${config.api_token}`);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }

  if (error) {
    return <section className="mx-auto max-w-7xl px-5 py-6"><Banner tone="danger" message={error} /></section>;
  }
  if (!config) {
    return <section className="mx-auto max-w-7xl px-5 py-6 text-sm text-ink/60">Загружаем настройки...</section>;
  }

  return (
    <section className="mx-auto max-w-7xl px-5 py-6">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold">Настройки интеграции</h1>
        <Tooltip text="Администратор, под которым открыт текущий доступ к панели.">
            <p className="soft-wrap mt-1 inline-flex text-sm text-ink/60">{config.admin_email}</p>
        </Tooltip>
      </div>
      <div className="grid gap-5 lg:grid-cols-[0.95fr_1.05fr]">
        <section className="border border-line bg-white">
          <SectionHeader icon={<KeyRound className="h-4 w-4" />} title="Токен API" tooltip="Bearer-токен для серверных запросов к API аудита. Он виден только после входа в админку." />
          <div className="p-4">
            <Tooltip text="Передавайте это значение в заголовке Authorization как Bearer-токен.">
              <div className="mb-3 overflow-auto border border-line bg-paper p-3 font-mono text-sm">
                <span className="soft-wrap">Bearer {config.api_token}</span>
              </div>
            </Tooltip>
            <Tooltip text="Скопировать готовый заголовок Authorization в буфер обмена.">
              <button className="button-primary" onClick={copyBearer} aria-label="Скопировать Bearer-токен">
                <Clipboard className="h-4 w-4" />
                {copied ? 'Скопировано' : 'Скопировать'}
              </button>
            </Tooltip>
          </div>
        </section>
        <section className="border border-line bg-white">
          <SectionHeader icon={<SlidersHorizontal className="h-4 w-4" />} title="Настройки аудитора" tooltip="Параметры серверной интеграции с API партнера, совместимым с OpenAI." />
          <div className="grid gap-3 p-4 text-sm">
            <Metric label="API партнера" value={config.partner_base_url || '-'} tooltip="Базовый URL, куда сервер отправляет запросы моделей и чатов." />
            <Metric label="Динамические модели" value={config.dynamic_models_enabled ? 'включены' : 'выключены'} tooltip="Если включено, можно передавать model ID из rout.my напрямую в auditor_models." />
            <div className="border border-line bg-white p-3">
              <Tooltip text="Модели, которые используются, если audit-запрос не передал auditor_models явно.">
                <div className="soft-wrap mb-2 text-[11px] uppercase leading-tight text-ink/45">Модели по умолчанию</div>
              </Tooltip>
              <div className="flex flex-wrap gap-2">
                {config.default_auditor_models.map((model) => (
                  <span className="badge badge-dark" key={model}>{model}</span>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
      <section className="mt-5 border border-line bg-white">
        <SectionHeader icon={<Activity className="h-4 w-4" />} title="Модели rout.my" tooltip="Живой список моделей, полученный с защищенного backend endpoint `/api/v1/models`." />
        <div className="max-h-[520px] overflow-auto">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <thead className="bg-ink text-left text-paper">
              <tr>
                <Th tooltip="Строковый идентификатор модели для поля auditor_models.">ID модели</Th>
                <Th tooltip="Человекочитаемое название, если провайдер его вернул.">Название</Th>
                <Th tooltip="Владелец или namespace модели по данным провайдера.">Владелец</Th>
              </tr>
            </thead>
            <tbody>
              {models.map((model) => (
                <tr className="border-t border-line" key={model.id}>
                  <Td><span className="soft-wrap font-mono">{model.id}</span></Td>
                  <Td>{model.name || '-'}</Td>
                  <Td>{model.owned_by || '-'}</Td>
                </tr>
              ))}
              {models.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-10 text-center text-ink/50">Провайдер не вернул моделей</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}

function AuditorRunRow({ run }: { run: AuditorRun }) {
  return (
    <div className="p-4">
      <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <Tooltip text="Ключ модели-аудитора из запроса или из настроек по умолчанию.">
            <div className="soft-wrap font-semibold">{run.auditor_model}</div>
          </Tooltip>
          <Tooltip text="Фактическая модель или провайдер, куда ушел запрос.">
            <div className="soft-wrap text-xs text-ink/55">{run.provider_model || run.provider || 'провайдер не указан'}</div>
          </Tooltip>
        </div>
        <StatusBadge status={run.status} />
      </div>
      {run.status === 'completed' ? (
        <div className="grid grid-cols-2 gap-2 text-sm xl:grid-cols-4">
          <Metric label="Победитель" value={winnerLabel(run.winner || '')} tooltip="Победитель по мнению конкретной модели-аудитора." compact />
          <Metric label="База" value={run.baseline_score ?? '-'} tooltip="Оценка базового ответа от этой модели." compact />
          <Metric label="Оптим." value={run.optimized_score ?? '-'} tooltip="Оценка оптимизированного ответа от этой модели." compact />
          <Metric label="Увер." value={run.confidence ?? '-'} tooltip="Уверенность аудитора в своем выборе, от 0 до 100." compact />
        </div>
      ) : (
        <div className="text-sm text-rust">{run.error_message || 'Запуск не завершился'}</div>
      )}
    </div>
  );
}

function TextPanel({
  title,
  text,
  tooltip,
  badge,
  tall = false
}: {
  title: string;
  text: string;
  tooltip: string;
  badge?: string;
  tall?: boolean;
}) {
  return (
    <section className="border border-line bg-white">
      <div className="flex flex-col gap-1 border-b border-line px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
        <Tooltip text={tooltip}>
          <h2 className="text-sm font-semibold">{title}</h2>
        </Tooltip>
        {badge && (
          <Tooltip text="Источник контекста и модель, если интеграция передала model_name.">
            <span className="soft-wrap max-w-full text-xs uppercase leading-4 text-ink/50 sm:max-w-[58%] sm:text-right">{badge}</span>
          </Tooltip>
        )}
      </div>
      <pre className={`text-panel-content whitespace-pre-wrap p-4 font-sans text-sm leading-6 text-ink/80 ${tall ? 'max-h-[520px]' : 'max-h-[360px]'} overflow-auto`}>
        {text}
      </pre>
    </section>
  );
}

function DiagnosticList({ title, tooltip, items = [] }: { title: string; tooltip: string; items?: string[] }) {
  return (
    <div>
      <Tooltip text={tooltip}>
        <h3 className="soft-wrap mb-2 text-sm font-semibold">{title}</h3>
      </Tooltip>
      {items.length ? (
        <ul className="space-y-2 text-sm text-ink/75">
          {items.map((item) => <li key={item} className="soft-wrap border-l-2 border-line pl-3">{item}</li>)}
        </ul>
      ) : (
        <div className="text-sm text-ink/45">нет данных</div>
      )}
    </div>
  );
}

function collectDiagnostics(runs: AuditorRun[]): Diagnostics {
  const out: Diagnostics = {};
  for (const run of runs) {
    const diagnostics = run.parsed_result?.diagnostics;
    if (!diagnostics) continue;
    for (const key of Object.keys(diagnostics) as (keyof Diagnostics)[]) {
      out[key] = [...(out[key] ?? []), ...(diagnostics[key] ?? [])];
    }
  }
  return out;
}

function StatusBadge({ status }: { status: string }) {
  const completed = status === 'completed';
  const failed = status === 'failed';
  const Icon = completed ? CheckCircle2 : failed ? CircleSlash : AlertTriangle;
  return (
    <Tooltip text={statusHints[status] ?? 'Неизвестный статус от API.'}>
      <span className={`badge ${completed ? 'badge-green' : failed ? 'badge-red' : 'badge-amber'}`}>
        <Icon className="h-3.5 w-3.5" />
        {statusLabel(status)}
      </span>
    </Tooltip>
  );
}

function WinnerBadge({ winner }: { winner: Winner }) {
  return (
    <Tooltip text={winnerHints[winner] ?? 'Победитель еще не определен.'}>
      <span className="badge badge-dark">{winnerLabel(winner)}</span>
    </Tooltip>
  );
}

function Metric({
  label,
  value,
  tooltip,
  compact = false
}: {
  label: string;
  value: string | number;
  tooltip: string;
  compact?: boolean;
}) {
  return (
    <div className={`border border-line bg-white ${compact ? 'p-2' : 'p-3'}`}>
      <Tooltip text={tooltip}>
        <div className="soft-wrap text-[11px] uppercase leading-tight text-ink/45">{label}</div>
      </Tooltip>
      <div className="soft-wrap mt-1 text-lg font-semibold leading-tight">{value}</div>
    </div>
  );
}

function SectionHeader({ title, icon, tooltip }: { title: string; icon: ReactElement; tooltip: string }) {
  return (
    <div className="flex items-start gap-2 border-b border-line px-4 py-3 text-sm font-semibold">
      <span className="shrink-0 pt-0.5">{icon}</span>
      <Tooltip text={tooltip}>
        <span className="soft-wrap">{title}</span>
      </Tooltip>
    </div>
  );
}

function DocsSection({ title, tooltip, children }: { title: string; tooltip: string; children: ReactNode }) {
  return (
    <section className="border border-line bg-white p-5 text-sm leading-6 text-ink/75">
      <Tooltip text={tooltip}>
        <h2 className="soft-wrap mb-3 text-xl font-semibold leading-tight text-ink">{title}</h2>
      </Tooltip>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function DocsTerm({ term, description }: { term: string; description: string }) {
  return (
    <div className="border border-line bg-paper p-3">
      <div className="soft-wrap mb-1 font-mono text-xs font-semibold text-ink">{term}</div>
      <div className="soft-wrap text-sm leading-5 text-ink/70">{description}</div>
    </div>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="overflow-auto border border-line bg-ink p-4 text-xs leading-5 text-paper">
      <code>{children}</code>
    </pre>
  );
}

function Code({ children }: { children: ReactNode }) {
  return <code className="soft-wrap border border-line bg-paper px-1.5 py-0.5 font-mono text-[0.92em] text-ink">{children}</code>;
}

function Banner({ message, tone }: { message: string; tone: 'danger' }) {
  return <div className="soft-wrap mb-4 border border-rust/40 bg-rust/10 p-3 text-sm text-rust">{message}</div>;
}

function Th({ children, tooltip }: { children: ReactNode; tooltip: string }) {
  return (
    <th className="px-4 py-3 align-top font-medium">
      <Tooltip text={tooltip}>
        <span className="soft-wrap">{children}</span>
      </Tooltip>
    </th>
  );
}

function Td({ children }: { children: ReactNode }) {
  return <td className="soft-wrap max-w-[18rem] px-4 py-3 align-top">{children}</td>;
}

function Tooltip({ text, children }: { text: string; children: ReactNode }) {
  return (
    <span className="tooltip-wrap">
      {children}
      <span className="tooltip-bubble" role="tooltip">{text}</span>
    </span>
  );
}

function roleCandidates(candidates: Candidate[] = []) {
  const baselineIndex = resolveCandidateIndex(candidates, 'baseline', 'full_context', 0);
  const optimizedFallback = candidates.findIndex((_, index) => index !== baselineIndex);
  const optimizedIndex = resolveCandidateIndex(candidates, 'optimized', 'reduced_context', optimizedFallback);
  return {
    baseline: candidates[baselineIndex],
    optimized: candidates[optimizedIndex]
  };
}

function resolveCandidateIndex(candidates: Candidate[], id: string, contextUsed: string, fallback: number) {
  const byID = candidates.findIndex((candidate) => candidate.id === id);
  if (byID >= 0) return byID;
  const byContext = candidates.findIndex((candidate) => candidate.context_used === contextUsed);
  if (byContext >= 0) return byContext;
  return fallback >= 0 ? fallback : -1;
}

function candidateBadge(candidate?: Candidate) {
  if (!candidate) return undefined;
  const parts = [contextLabel(candidate.context_used)].filter(Boolean) as string[];
  if (candidate.model_name) {
    parts.push(candidate.model_name);
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function scorePair(audit: AuditRecord) {
  const a = audit.baseline_score ?? '-';
  const b = audit.optimized_score ?? '-';
  return `${a} / ${b}`;
}

function statusLabel(status: string) {
  return statusLabels[status] ?? status;
}

function winnerLabel(winner: string) {
  return winnerLabels[winner] ?? '-';
}

function contextLabel(value?: string) {
  if (!value) return undefined;
  return contextLabels[value] ?? value;
}

function consensusLabel(value?: string) {
  if (!value) return '-';
  return consensusLabels[value] ?? value;
}

function pluralAudits(value: number) {
  const mod10 = value % 10;
  const mod100 = value % 100;
  const noun = mod10 === 1 && mod100 !== 11
    ? 'аудит'
    : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)
      ? 'аудита'
      : 'аудитов';
  return `${value} ${noun}`;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('ru-RU', {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(value));
}
